import { useState } from "react";
import { submitReview } from "@/services/publicApi";
import type { Review } from "@/types";

const MAX_COMMENT = 500;

interface ReviewFormProps {
  businessId: string;
  businessName: string;
  onSubmitted: (review: Review) => void;
}

const inputClass =
  "w-full rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none";
const labelClass = "mb-1.5 block text-xs font-semibold uppercase tracking-wide text-foreground-500";

/**
 * Public review submission for a listing (§13). Ratings are chosen with an
 * accessible star control; the server holds the review for moderation and it
 * only appears on the profile once published.
 */
export default function ReviewForm({ businessId, businessName, onSubmitted }: ReviewFormProps) {
  const [open, setOpen] = useState(false);
  const [rating, setRating] = useState(0);
  const [hover, setHover] = useState(0);
  const [name, setName] = useState("");
  const [comment, setComment] = useState("");
  const [status, setStatus] = useState<"idle" | "submitting" | "success" | "error">("idle");
  const [feedback, setFeedback] = useState("");

  const reset = () => {
    setRating(0);
    setHover(0);
    setName("");
    setComment("");
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const fd = new FormData(form);

    // Honeypot: silently accept so bots learn nothing.
    if (String(fd.get("website_alt") || "").trim()) {
      setStatus("success");
      setFeedback("Thank you! Your review has been received.");
      form.reset();
      reset();
      return;
    }

    if (rating < 1) {
      setStatus("error");
      setFeedback("Please pick a star rating before submitting.");
      return;
    }

    setStatus("submitting");
    setFeedback("");
    try {
      const review = await submitReview({
        businessId,
        businessName,
        authorName: name.trim(),
        rating,
        comment: comment.trim(),
      });
      onSubmitted(review);
      setStatus("success");
      setFeedback("Thank you! Your review is pending approval and will appear once published.");
      form.reset();
      reset();
    } catch (err) {
      setStatus("error");
      setFeedback(err instanceof Error ? err.message : "We couldn't submit your review. Please try again.");
    }
  };

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="inline-flex items-center gap-2 rounded-md bg-primary-500 px-4 py-2.5 text-sm font-bold text-foreground-950 hover:bg-primary-600 whitespace-nowrap cursor-pointer"
      >
        <i className="ri-edit-2-line"></i> Write a review
      </button>
    );
  }

  return (
    <div className="rounded-lg border border-background-200 bg-background-50 p-5">
      <div className="flex items-center justify-between">
        <h3 className="font-heading text-base font-bold text-foreground-950">Write a review</h3>
        <button
          type="button"
          onClick={() => {
            setOpen(false);
            setStatus("idle");
            setFeedback("");
          }}
          className="flex h-8 w-8 items-center justify-center rounded-md text-foreground-500 hover:bg-background-100 cursor-pointer"
          aria-label="Close review form"
        >
          <i className="ri-close-line text-lg"></i>
        </button>
      </div>

      <form onSubmit={handleSubmit} className="mt-4 space-y-4">
        <div>
          <span className={labelClass}>Your rating *</span>
          <div className="flex items-center gap-1">
            {[1, 2, 3, 4, 5].map((star) => {
              const active = (hover || rating) >= star;
              return (
                <button
                  key={star}
                  type="button"
                  onClick={() => setRating(star)}
                  onMouseEnter={() => setHover(star)}
                  onMouseLeave={() => setHover(0)}
                  aria-label={`${star} star${star > 1 ? "s" : ""}`}
                  className="cursor-pointer p-0.5"
                >
                  <i className={`${active ? "ri-star-fill text-primary-500" : "ri-star-line text-background-400"} text-2xl`}></i>
                </button>
              );
            })}
            <span className="ml-2 text-sm font-semibold text-foreground-700">{rating ? `${rating}.0` : "Select"}</span>
          </div>
        </div>

        <div>
          <label htmlFor="review-name" className={labelClass}>Your name *</label>
          <input
            id="review-name"
            name="name"
            required
            maxLength={120}
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. Sneha Rao"
            className={inputClass}
          />
        </div>

        <div>
          <div className="flex items-center justify-between">
            <label htmlFor="review-comment" className={labelClass}>Your review *</label>
            <span className="text-[11px] text-foreground-400">{comment.length}/{MAX_COMMENT}</span>
          </div>
          <textarea
            id="review-comment"
            name="comment"
            required
            rows={4}
            maxLength={MAX_COMMENT}
            value={comment}
            onChange={(e) => setComment(e.target.value.slice(0, MAX_COMMENT))}
            placeholder={`Share your experience with ${businessName}…`}
            className={`${inputClass} resize-none`}
          ></textarea>
        </div>

        <input
          type="text"
          name="website_alt"
          className="form-aux-field"
          tabIndex={-1}
          autoComplete="off"
          aria-hidden="true"
          readOnly
        />

        {status === "success" && (
          <div className="flex items-start gap-3 rounded-md border border-accent-200 bg-accent-50 p-4 text-sm text-accent-900">
            <i className="ri-checkbox-circle-line mt-0.5 text-lg text-accent-600"></i>
            <span>{feedback}</span>
          </div>
        )}
        {status === "error" && (
          <div className="flex items-start gap-3 rounded-md border border-secondary-200 bg-secondary-50 p-4 text-sm text-secondary-900">
            <i className="ri-error-warning-line mt-0.5 text-lg text-secondary-600"></i>
            <span>{feedback}</span>
          </div>
        )}

        <div className="flex flex-wrap items-center gap-3">
          <button
            type="submit"
            disabled={status === "submitting"}
            className="inline-flex items-center justify-center gap-2 rounded-md bg-primary-500 px-5 py-2.5 text-sm font-bold text-foreground-950 hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60 whitespace-nowrap cursor-pointer"
          >
            {status === "submitting" ? (
              <>
                <i className="ri-loader-4-line animate-spin"></i> Submitting…
              </>
            ) : (
              <>
                <i className="ri-send-plane-line"></i> Submit review
              </>
            )}
          </button>
          <p className="text-xs text-foreground-500">Reviews are moderated before they appear.</p>
        </div>
      </form>
    </div>
  );
}